import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";

export default function NotificationsBell({ user, buttonStyle = {} }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loadError, setLoadError] = useState("");
  const rootRef = useRef(null);
  const userEmail = String(user?.email || "").trim();
  const emailVariants = useMemo(
    () => [...new Set([userEmail, userEmail.toLowerCase()].filter(Boolean))],
    [userEmail],
  );

  useEffect(() => {
    if (!emailVariants.length) {
      setItems([]);
      setLoadError("");
      return undefined;
    }
    const bucketByVariant = new Map(emailVariants.map((variant) => [variant, new Map()]));
    const applyRows = () => {
      const merged = new Map();
      bucketByVariant.forEach((bucket) => {
        bucket.forEach((row, id) => merged.set(id, row));
      });
      const list = [...merged.values()];
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setItems(list.slice(0, 25));
    };

    const unsubscribers = emailVariants.map((variant) => {
      const notificationsQuery = query(
        collection(db, "notifications"),
        where("recipientEmail", "==", variant),
      );
      return onSnapshot(
        notificationsQuery,
        (snapshot) => {
          const bucket = bucketByVariant.get(variant);
          if (!bucket) return;
          bucket.clear();
          snapshot.docs.forEach((item) => {
            bucket.set(item.id, { id: item.id, ...item.data() });
          });
          applyRows();
          setLoadError("");
        },
        (error) => {
          setLoadError(error.message || "Unable to load notifications.");
          applyRows();
        },
      );
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [emailVariants]);
  useEffect(() => {
    const onDocClick = (event) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const unread = useMemo(
    () => items.filter((n) => !n.read).length,
    [items],
  );

  const markRead = async (id) => {
    try {
      await updateDoc(doc(db, "notifications", id), { read: true });
    } catch (error) {
      setLoadError(error.message || "Unable to update notification.");
    }
  };

  const markAllRead = async () => {
    try {
      const updates = items
        .filter((n) => !n.read)
        .map((n) => updateDoc(doc(db, "notifications", n.id), { read: true }));
      await Promise.all(updates);
    } catch (error) {
      setLoadError(error.message || "Unable to mark all as read.");
    }
  };

  if (!emailVariants.length) return null;

  const triggerStyle = {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    lineHeight: 1,
    ...buttonStyle,
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        style={triggerStyle}
      >
        Notifications
        {unread > 0 && (
          <span
            style={{
              marginLeft: 6,
              background: "#e11d48",
              color: "#fff",
              borderRadius: 999,
              padding: "1px 7px",
              fontSize: 12,
            }}
          >
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications panel"
          style={{
            position: "absolute",
            right: 0,
            top: "105%",
            width: "min(360px, 92vw)",
            maxHeight: 420,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #dbe3ee",
            borderRadius: 10,
            boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
            padding: 10,
            zIndex: 20,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <strong>Notifications</strong>
            <button onClick={markAllRead}>Mark all read</button>
          </div>
          {loadError && (
            <div style={{ border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", borderRadius: 8, padding: 8, marginBottom: 8 }}>
              {loadError}
            </div>
          )}

          {items.length === 0 ? (
            <p style={{ color: "#64748b" }}>No notifications yet.</p>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 10,
                  marginBottom: 8,
                  background: n.read ? "#fff" : "#f8fbff",
                }}
              >
                <p style={{ margin: 0, fontWeight: 600 }}>{n.title || "Update"}</p>
                {n.type && (
                  <small style={{ color: "#1d4ed8", fontWeight: 600 }}>{String(n.type).replaceAll("_", " ")}</small>
                )}
                <p style={{ margin: "4px 0", color: "#475569" }}>{n.message || ""}</p>
                {n.createdAt?.seconds && (
                  <small style={{ color: "#64748b" }}>
                    {new Date(n.createdAt.seconds * 1000).toLocaleString()}
                  </small>
                )}
                {!n.read && <button onClick={() => markRead(n.id)}>Mark read</button>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
